import React from 'react';
import { Button, Typography, SxProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

interface CustomIconButtonProps {
  icon?: React.ReactNode; // Optional icon
  text?: string;
  outline?: boolean;
  background?: string;
  variant?: 'contained' | 'outlined';
  padding?: string;
  color?: string;
  isDisabled?: boolean;
  size?: number;
  textColor?: string;
  height?: string | { xs?: string; sm?: string; md?: string };
  width?: string | { xs?: string; sm?: string; md?: string };
  minWidth?: string | { xs?: string; sm?: string; md?: string };
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void; // Fixed type here
  type?: 'button' | 'submit' | 'reset';
  sx?: SxProps<Theme>;
}

const CustomIconButton: React.FC<CustomIconButtonProps> = ({
  icon,
  text,
  size,
  outline = true,
  background = '',
  variant = 'contained',
  padding,
  color,
  isDisabled = false,
  height,
  width,
  minWidth,
  textColor,
  onClick,
  type,
  sx,
}) => {
  const isTransparent = background === 'transparent';
  const isError = background === 'error';

  return (
    <Button
      variant={variant}
      onClick={(e) => {
        if (onClick) onClick(e);
      }}
      disabled={isDisabled}
      type={type}
      sx={(theme) => {
        const defaultSx: SystemStyleObject<Theme> = {
          height: height || { xs: '28px', sm: '30px', md: '45px' },
          width: width,
          minWidth: minWidth,
          px: { xs: 1, sm: 2 },
          borderRadius: '8px',
          textTransform: 'none' as const,
          fontWeight: 700,
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: icon ? { xs: 0.5, sm: 1 } : 0,
          backgroundColor: isTransparent
            ? 'transparent'
            : isError
              ? theme.palette.error.main
              : theme.palette.primary.main,
          border: outline && !isDisabled ? `1px solid ${theme.palette.primary.main}` : 'none',
          color:
            color == 'error'
              ? theme.palette.error.main
              : isTransparent
                ? `${theme.palette.primary.main}`
                : 'white',
          padding: padding || undefined,
          '&:hover': {
            backgroundColor: isTransparent ? 'transparent' : isError ? theme.palette.error.dark : '#4039b3',
            boxShadow: 'none',
          },
        };

        const customSx = typeof sx === 'function' ? sx(theme) : sx || {};
        return { ...defaultSx, ...customSx } as SystemStyleObject<Theme>;
      }}
    >
      {icon && <>{icon}</>}
      {text && (
        <Typography
          color={textColor}
          variant={isTransparent ? 'overline' : 'body2'}
          fontWeight={700}
          fontSize={size}
          sx={{
            fontFamily: 'Inter Tight',
            fontWeight: 700,
            fontSize: size || '16px',
            lineHeight: '24px',
            color: textColor || (isTransparent ? '#5547DD' : 'inherit'),
          }}
        >
          {text}
        </Typography>
      )}
    </Button>
  );
};

export default CustomIconButton;
